import React, { useRef } from 'react';
import { PinnedHScroll } from './PinnedHScroll';
import { AbawdCell, CertPeriodCell } from './ReferralFieldCells';
import { formatDate } from '../../utils/dateFormat';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, Link2, Phone, Mail, MessageSquare, FileText, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_TONE = {
  Enrolled: 'bg-green-100 text-green-800 border-green-200',
  'DSS Approved': 'bg-green-100 text-green-800 border-green-200',
  Declined: 'bg-red-100 text-red-800 border-red-200',
  'Denied Eligibility': 'bg-red-100 text-red-800 border-red-200',
  'FNS Not-Eligible': 'bg-red-100 text-red-800 border-red-200',
  'Initial Contact': 'bg-blue-100 text-blue-800 border-blue-200',
  'Pending Contact': 'bg-slate-100 text-slate-700 border-slate-200',
};

const showDate = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? formatDate(d) : d || '');

const ActionIcon = ({ icon: Icon, label, onClick, disabled, testId }) => (
  <Button variant="ghost" size="icon" className="h-6 w-6" title={label} onClick={onClick} disabled={disabled} data-testid={testId}>
    <Icon className="w-3.5 h-3.5" />
  </Button>
);

const ContactCell = ({ record, contact, ctx }) => {
  const n = contact.n;
  const rid = record.id;
  if (record.locked_from != null && n > record.locked_from) {
    return (
      <div className="min-w-[150px] rounded-md px-2 py-1.5 bg-muted/60 text-muted-foreground opacity-70 cursor-not-allowed" data-testid={`rt-contact-${n}-${rid}`} data-unavailable="true">
        <p className="text-xs flex items-center gap-1"><Ban className="w-3 h-3" />Unavailable</p>
        <p className="text-[11px]">Outreach ended</p>
      </div>
    );
  }
  const canAct = ctx.isManager || contact.assigned_user_id === ctx.me;
  const linked = !!record.participant_id;
  const blockedMsg = !linked ? 'Link to a profile first' : !canAct ? 'Only the assigned user or a manager' : '';
  const act = (kind) => ctx.onAction(record, n, kind);
  return (
    <div className="min-w-[150px]">
      <button type="button" onClick={() => ctx.onEditContact(record, n)} className="text-left w-full rounded-md px-2 py-1.5 hover:bg-muted/70 transition-colors" data-testid={`rt-contact-${n}-${rid}`}>
        <p className="text-xs font-medium truncate">{contact.assigned_user_name || <span className="text-muted-foreground">Unassigned</span>}</p>
        <p className="text-xs text-muted-foreground">{showDate(contact.date) || '—'}</p>
        {contact.status
          ? <Badge variant="outline" className={`mt-0.5 text-[11px] ${STATUS_TONE[contact.status] || 'bg-amber-50 text-amber-800 border-amber-200'}`}>{contact.status}</Badge>
          : <span className="text-[11px] text-muted-foreground">No status</span>}
      </button>
      <div className="flex gap-0.5 px-1" title={blockedMsg}>
        {n >= 2 && <ActionIcon icon={Phone} label="Call" disabled={!linked || !canAct} onClick={() => act('call')} testId={`rt-call-${n}-${rid}`} />}
        <ActionIcon icon={Mail} label="Email" disabled={!linked || !canAct} onClick={() => act('email')} testId={`rt-email-${n}-${rid}`} />
        <ActionIcon icon={MessageSquare} label="Text" disabled={!linked || !canAct} onClick={() => act('sms')} testId={`rt-sms-${n}-${rid}`} />
        <ActionIcon icon={FileText} label="Add Case Note" disabled={!linked || !canAct} onClick={() => act('note')} testId={`rt-note-${n}-${rid}`} />
      </div>
    </div>
  );
};

const STICKY = { 3: 'sticky left-0 w-32 min-w-[8rem]', 4: 'sticky left-32 w-36 min-w-[9rem] shadow-[2px_0_0_0_hsl(var(--border))]' };
const COLS = ['Referral Date', 'County', 'Referral Type', 'First Name', 'Last Name', 'Case Number', 'ABAWD Status', 'Certification Period', 'Email', 'Initial Contact', 'Contact 2', 'Contact 3', 'Contact 4'];

const Contact4Cell = ({ record, contact, ctx }) => {
  if (contact) return <ContactCell record={record} contact={contact} ctx={ctx} />;
  if (record.locked_from != null) return <span className="text-[11px] text-muted-foreground px-2" data-testid={`rt-contact4-na-${record.id}`}>—</span>;
  if (!ctx.isManager) return null;
  return <Button variant="ghost" size="sm" className="text-xs" onClick={() => ctx.onEditContact(record, 4)} data-testid={`rt-add-contact4-${record.id}`}><Plus className="w-3 h-3 mr-1" />Add Contact 4</Button>;
};

export const ReferralTable = ({ records, ctx }) => {
  const wrapRef = useRef(null);
  if (!records.length) {
    return <p className="text-center text-sm text-muted-foreground py-12" data-testid="rt-empty">No referrals for these filters yet.</p>;
  }
  return (
    <>
    <div ref={wrapRef} className="overflow-auto max-h-[calc(100vh-7rem)] overscroll-x-contain rounded-md border border-border" data-testid="rt-table-wrap">
      <table className="w-full text-sm border-separate border-spacing-0" data-testid="rt-table">
        <thead>
          <tr>
            {COLS.map((c, i) => <th key={c} className={`${STICKY[i] ? `${STICKY[i]} z-30` : 'z-20'} sticky top-0 bg-muted border-b border-border text-left py-2.5 px-3 text-xs font-semibold uppercase text-muted-foreground whitespace-nowrap`}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const byN = Object.fromEntries((r.contacts || []).map((c) => [c.n, c]));
            return (
              <tr key={r.id} className="align-top hover:bg-muted/30 [&>td]:border-b [&>td]:border-border" data-testid={`rt-row-${r.id}`}>
                <td className="py-2 px-3 whitespace-nowrap">{showDate(r.referral_date)}</td>
                <td className="py-2 px-3 whitespace-nowrap">{r.county || ''}</td>
                <td className="py-2 px-3 whitespace-nowrap">{r.referral_type || ''}</td>
                <td className={`${STICKY[3]} z-10 bg-background py-2 px-3 whitespace-nowrap font-medium`}>{r.first_name}</td>
                <td className={`${STICKY[4]} z-10 bg-background py-2 px-3 whitespace-nowrap font-medium`}>
                  {r.last_name}
                  {r.ssn_incomplete && (
                    <Badge variant="outline" className="block w-fit mt-0.5 text-[10px] whitespace-normal leading-tight bg-amber-50 text-amber-800 border-amber-300" data-testid={`rt-ssn-incomplete-${r.id}`}>
                      SSN incomplete - last 4 only (***-**-{r.ssn_last4})
                    </Badge>
                  )}
                  {r.participant_id
                    ? <Link to={`/participants/${r.participant_id}`} className="inline-flex ml-1 text-primary align-middle" title="Open profile" data-testid={`rt-profile-link-${r.id}`}><Link2 className="w-3.5 h-3.5" /></Link>
                    : <Button variant="link" size="sm" className="h-auto p-0 ml-1 text-xs block" onClick={() => ctx.onLink(r)} data-testid={`rt-link-profile-${r.id}`}>Link to profile</Button>}
                </td>
                <td className="py-2 px-3">{r.case_number || ''}</td>
                <td className="py-1 px-2"><AbawdCell record={r} options={ctx.options} onSaved={ctx.onRowSaved} /></td>
                <td className="py-1 px-2"><CertPeriodCell record={r} onSaved={ctx.onRowSaved} /></td>
                <td className="py-2 px-3 whitespace-nowrap">{r.email || ''}</td>
                {[1, 2, 3].map((n) => <td key={n} className="py-1 px-1"><ContactCell record={r} contact={byN[n] || { n }} ctx={ctx} /></td>)}
                <td className="py-1 px-1"><Contact4Cell record={r} contact={byN[4]} ctx={ctx} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <PinnedHScroll targetRef={wrapRef} testId="rt-pinned-hscroll" />
    </>
  );
};
